import { Vec2 } from '../math/Vec2';
import { ENGINE, BALL, POWERUP, SCORES } from '../config/constants';
import { BOT_COLORS, getColorName } from '../config/colors';
import { Arena } from '../entities/Arena';
import { Paddle } from '../entities/Paddle';
import { Ball } from '../entities/Ball';
import { PowerUp, PowerUpType } from '../entities/PowerUp';
import { GameState } from './GameState';
import { EventBus } from './EventBus';
import { CollisionSystem, CollisionEventData } from '../physics/CollisionSystem';
import { BotController } from '../ai/BotController';
import { ParticleSystem } from '../render/ParticleSystem';
import { CanvasRenderer } from '../render/CanvasRenderer';
import { InputManager } from '../input/InputManager';
import { ScoreManager } from '../storage/ScoreManager';
import { SoundFx } from '../audio/SoundFx';
import { AudioSequencer } from '../audio/AudioSequencer';
import { HUD } from '../ui/HUD';
import { Menu } from '../ui/Menu';

export class Engine {
  private arena: Arena;
  private state: GameState;
  private particles: ParticleSystem;
  private renderer: CanvasRenderer;
  private input: InputManager;
  private scoreManager: ScoreManager;
  private sequencer: AudioSequencer;
  private hud: HUD;
  private menu: Menu;
  private audioCtx: AudioContext | null = null;

  private isRunning: boolean = false;
  private lastTime: number = 0;
  private accumulator: number = 0;
  private countdownInterval: number | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    menuContainer: HTMLElement,
    hudContainer: HTMLElement
  ) {
    this.arena = new Arena();
    this.state = new GameState();
    this.particles = new ParticleSystem();
    this.renderer = new CanvasRenderer(canvas);
    this.input = new InputManager();
    this.scoreManager = new ScoreManager();
    this.sequencer = new AudioSequencer();

    this.hud = new HUD(hudContainer, (muted: boolean) => {
      SoundFx.setMuted(muted);
      this.sequencer.setMuted(muted);
    });

    this.menu = new Menu(menuContainer, this.scoreManager, (count: number) => {
      this.startSession(count);
    });

    this.setupEvents();
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());

    this.isRunning = true;
    this.lastTime = performance.now();
    requestAnimationFrame(time => this.loop(time));
  }

  private handleResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.resize(width, height);
    this.arena.resize(width, height);
  }

  private setupEvents(): void {
    EventBus.on<CollisionEventData>('ballBounce', data => {
      SoundFx.playBounce(data.speed, data.isSmash, data.isSweetSpot);

      if (data.isSweetSpot) {
        this.particles.addScreenShake(25);
        this.particles.spawnParticles(data.point, '#ffcc00', 45);
        this.particles.spawnRipples(data.point, '#ffcc00', true);

        if (data.paddle?.isHuman) {
          this.addScore(SCORES.SWEET_SPOT, 'SWEET SPOT!!', data.point);
        }
      } else if (data.isSmash) {
        this.particles.addScreenShake(20);
        this.particles.spawnParticles(data.point, '#ffffff', 60);
        if (data.paddle) {
          this.particles.spawnParticles(data.point, data.paddle.color, 25);
        }
        this.particles.spawnRipples(data.point, '#ffffff', true);

        if (data.paddle?.isHuman) {
          this.addScore(SCORES.SMASH_HIT, 'SMASH! +50', data.point);
        }
      } else {
        const col = data.paddle ? data.paddle.color : '#ffffff';
        this.particles.spawnParticles(data.point, col, 25);
        this.particles.spawnRipples(data.point, col, false);
        this.particles.addScreenShake(4);

        if (data.paddle?.isHuman) {
          this.addScore(SCORES.PADDLE_HIT, '+10', data.point);
        }
      }
    });

    EventBus.on<{ powerup: PowerUp; hitter?: Paddle }>('powerupCollected', data => {
      SoundFx.playPowerup();
      this.particles.spawnParticles(data.powerup.pos, data.powerup.color, 35);

      if (data.powerup.type === PowerUpType.MULTIBALL) {
        // Spawn 2 extra balls
        for (let i = 0; i < 2; i++) {
          const extra = new Ball(data.powerup.pos, false);
          const angle = Math.random() * Math.PI * 2;
          const speed = 5 + Math.random() * 5;
          extra.vel = Vec2.fromAngle(angle, speed);
          this.state.balls.push(extra);
        }
      }
    });

    EventBus.on<{ delta: number; reason: string; pos?: Vec2 }>('scoreAdded', data => {
      this.addScore(data.delta, data.reason, data.pos);
    });

    EventBus.on<{ paddle: Paddle; lives: number }>('playerHealed', data => {
      if (data.paddle.isHuman) {
        this.hud.setLives(data.lives);
      }
    });
  }

  private addScore(delta: number, reason: string, pos?: Vec2): void {
    this.state.score += delta;
    this.hud.setScore(this.state.score);
    this.particles.registerScoreRoll(delta, this.state.frameCounter);

    const displayPos = pos || this.arena.center;
    this.particles.spawnFloatingText(displayPos, reason, '#00ffcc', 26, 75);

    // High score banner check
    const isNewHighScore =
      (this.scoreManager.bestHighScore > 0 && this.state.score > this.scoreManager.bestHighScore) ||
      (this.scoreManager.bestHighScore === 0 && this.state.score >= 500);

    if (!this.state.hasShownHighScoreBanner && isNewHighScore) {
      this.state.hasShownHighScoreBanner = true;
      this.hud.showHighScoreBanner(this.state.score);
    }
  }

  startSession(playerCount: number): void {
    // Initialize Web Audio on user gesture (reuse single context)
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        if (!this.audioCtx) {
          this.audioCtx = new AudioContextClass();
          SoundFx.init(this.audioCtx);
          this.sequencer.init(this.audioCtx);
        }
        if (this.audioCtx.state === 'suspended') {
          this.audioCtx.resume().catch(() => {});
        }
      }
    } catch {
      // Audio fallback
    }

    this.scoreManager.clearHighlight();
    this.state.reset(playerCount);
    this.particles.clear();
    this.handleResize();

    this.menu.hide();
    this.hud.show();
    this.hud.setScore(0);
    this.hud.setLives(3);

    this.setupArenaPaddles(playerCount);
    this.resetBalls();

    this.sequencer.updatePlayerCount(playerCount);
    this.sequencer.stop();
    this.sequencer.start();

    this.triggerCountdown('GET READY!', () => {
      this.state.phase = 'PLAYING';
      this.launchBall();
    });

    if (!this.isRunning) {
      this.isRunning = true;
      this.lastTime = performance.now();
      requestAnimationFrame(time => this.loop(time));
    }
  }

  private setupArenaPaddles(count: number, survivingBot?: Paddle): void {
    this.state.paddles = [];

    if (count === 2) {
      // 1v1 Boss Duel Arena (Square: Human, Wall, Bot, Wall)
      let human = this.state.humanPlayer;
      if (!human) {
        human = new Paddle({
          id: 'human',
          isHuman: true,
          color: '#00ffcc',
        });
      } else {
        human.pos = 0.5;
        human.vel = 0;
        human.shieldTimer = 0;
        human.freezeTimer = 0;
      }

      let bot = survivingBot;
      if (!bot) {
        bot = new Paddle({
          id: 'bot_duel',
          isHuman: false,
          color: BOT_COLORS[0],
        });
      } else {
        bot.pos = 0.5;
        bot.vel = 0;
        bot.shieldTimer = 0;
        bot.freezeTimer = 0;
      }

      this.state.paddles = [
        human,
        new Paddle({ id: 'w1', isHuman: false, color: 'rgba(255,255,255,0.06)', isWall: true }),
        bot,
        new Paddle({ id: 'w2', isHuman: false, color: 'rgba(255,255,255,0.06)', isWall: true }),
      ];
    } else {
      for (let i = 0; i < count; i++) {
        this.state.paddles.push(
          new Paddle({
            id: `p_${i}_${Math.random().toString(36).substr(2, 6)}`,
            isHuman: i === 0,
            color: i === 0 ? '#00ffcc' : BOT_COLORS[(i - 1) % BOT_COLORS.length],
          })
        );
      }
    }
  }

  private resetBalls(): void {
    const mainBall = new Ball(this.arena.center, true);
    this.state.balls = [mainBall];
  }

  private launchBall(): void {
    const mainBall = this.state.balls.find(b => b.isMain);
    if (!mainBall) return;

    mainBall.pos = this.arena.center.clone();
    const playerCount = this.state.paddles.length;

    if (playerCount > 0) {
      const straightDownAngle = Math.PI / 2;
      const maxSpread = Math.PI / playerCount;
      const safeSpread = maxSpread * 0.9;
      const spread = (Math.random() - 0.5) * 2 * safeSpread;
      const targetAngle = straightDownAngle + spread;

      mainBall.launch(targetAngle, BALL.INITIAL_SPEED);
    } else {
      mainBall.launch(Math.PI / 2, BALL.INITIAL_SPEED);
    }
  }

  private triggerCountdown(message: string, callback: () => void): void {
    this.state.isCountingDown = true;
    this.state.countdownValue = 3;
    this.state.canvasMessage = message;
    this.resetBalls();

    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = window.setInterval(() => {
      this.state.countdownValue--;
      if (this.state.countdownValue <= 0) {
        if (this.countdownInterval !== null) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        this.state.isCountingDown = false;
        callback();
      }
    }, 1000);
  }

  private handleLoss(loserIndex: number, ball: Ball): void {
    const loser = this.state.paddles[loserIndex];
    if (!loser || loser.isWall) return;

    ball.isDead = true;
    ball.vel = Vec2.zero();

    // Check if there are other balls currently in play (excluding this ball)
    const otherAliveBalls = this.state.balls.filter(b => b !== ball && !b.isDead);
    const hasOtherBalls = otherAliveBalls.length > 0;

    if (hasOtherBalls && ball.isMain) {
      // Promote the first remaining alive ball to main ball
      otherAliveBalls[0].isMain = true;
    }

    if (hasOtherBalls) {
      // Multiball extra ball loss: despawn this ball without interrupting match play
      this.particles.spawnParticles(ball.pos, '#ffff00', 20);

      if (!loser || loser.isWall) return;

      this.particles.addScreenShake(12);
      SoundFx.playDamage();
      this.arena.triggerHitLine(loserIndex, loser.color);

      if (ball.lastHitterId && ball.lastHitterId !== loser.id) {
        const killer = this.state.paddles.find(p => p.id === ball.lastHitterId);
        if (killer && killer.isHuman) {
          this.addScore(SCORES.BOT_KILL, 'KILL! +500', this.arena.center);
        }
      }

      const { livesLeft, eliminated } = loser.applyDamage();
      const botName = getColorName(loser.color);

      if (loser.isHuman) {
        this.hud.setLives(livesLeft);
        if (eliminated) {
          this.endGame('YOU WERE ELIMINATED! GAME OVER.');
          return;
        }
        this.particles.spawnFloatingText(this.arena.center, `DAMAGE TAKEN! (${livesLeft} LIVES LEFT)`, '#ff0055', 24, 60);
      } else {
        if (eliminated) {
          this.state.paddles.splice(loserIndex, 1);
          if (this.state.paddles.length === 2) {
            const survivingBot = this.state.paddles[1];
            this.setupArenaPaddles(2, survivingBot);
          }
          this.sequencer.updatePlayerCount(this.state.activeCombatants.length);
          const activeBotsCount = this.state.activeBots.length;
          if (activeBotsCount === 0) {
            this.addScore(SCORES.CHAMPION_BONUS, 'CHAMPION! +2000', this.arena.center);
            this.endGame('👑 BATTLE ROYALE WINNER! You are the last remaining.');
            return;
          } else {
            this.addScore(SCORES.SURVIVAL_BONUS, 'SURVIVAL! +500', this.arena.center);
            this.particles.spawnFloatingText(this.arena.center, `${botName} ELIMINATED!`, '#00ffcc', 26, 75);
          }
        } else {
          this.particles.spawnFloatingText(this.arena.center, `${botName} TOOK DAMAGE!`, loser.color, 22, 60);
        }
      }
      return;
    }

    if (!loser || loser.isWall) {
      this.triggerCountdown('INVALID HIT!', () => this.launchBall());
      return;
    }

    this.particles.addScreenShake(25);
    SoundFx.playDamage();
    this.arena.triggerHitLine(loserIndex, loser.color);

    // Kill Bonus
    if (ball.lastHitterId && ball.lastHitterId !== loser.id) {
      const killer = this.state.paddles.find(p => p.id === ball.lastHitterId);
      if (killer && killer.isHuman) {
        this.addScore(SCORES.BOT_KILL, 'KILL! +500', this.arena.center);
      }
    }

    const { livesLeft, eliminated } = loser.applyDamage();
    const botName = getColorName(loser.color);

    if (loser.isHuman) {
      this.hud.setLives(livesLeft);
    }

    if (!eliminated) {
      const msg = loser.isHuman
        ? `DAMAGE TAKEN! (${livesLeft} LIVES LEFT)`
        : `${botName} TOOK DAMAGE!`;
      this.triggerCountdown(msg, () => this.launchBall());
      return;
    }

    // Elimination Flow
    if (loser.isHuman) {
      this.endGame('YOU WERE ELIMINATED! GAME OVER.');
    } else {
      const humanAlive = this.state.humanPlayer && this.state.humanPlayer.isAlive;
      this.state.paddles.splice(loserIndex, 1);

      // Transition to 1v1 duel arena if down to 2 players
      if (this.state.paddles.length === 2) {
        const survivingBot = this.state.paddles[1];
        this.setupArenaPaddles(2, survivingBot);
      }

      const activeBotsCount = this.state.activeBots.length;
      this.sequencer.updatePlayerCount(this.state.activeCombatants.length);

      if (humanAlive) {
        if (activeBotsCount === 0) {
          // Champion Victory!
          this.addScore(SCORES.CHAMPION_BONUS, 'CHAMPION! +2000', this.arena.center);
          this.endGame('👑 BATTLE ROYALE WINNER! You are the last remaining.');
        } else {
          // Survival bonus
          this.addScore(SCORES.SURVIVAL_BONUS, 'SURVIVAL! +500', this.arena.center);
          this.triggerCountdown(`${botName} ELIMINATED!\nARENA SHRINKING!`, () => {
            this.launchBall();
          });
        }
      }
    }
  }

  private endGame(message: string): void {
    this.state.phase = 'GAME_OVER';
    this.sequencer.stop();

    const record = this.scoreManager.recordScore(this.state.score);
    this.hud.hide();

    let fullMsg = message;
    if (record.added && record.rank === 0) {
      fullMsg = `🔥 NEW HIGH SCORE! 🔥\n${message}\nFINAL SCORE: ${this.state.score.toLocaleString()}`;
    } else {
      fullMsg = `${message}\nFINAL SCORE: ${this.state.score.toLocaleString()}`;
    }

    this.menu.show(fullMsg);
  }

  // --- DETERMINISTIC 60HZ FIXED TIMESTEP LOOP ---
  private loop(now: number): void {
    const frameTime = Math.min((now - this.lastTime) / 1000, ENGINE.MAX_FRAME_TIME);
    this.lastTime = now;
    this.accumulator += frameTime;

    // Check space key to start game from menu
    if (this.state.phase === 'MENU' || this.state.phase === 'GAME_OVER') {
      if (this.input.consumeSpace()) {
        const sel = document.getElementById('playerCount') as HTMLSelectElement;
        const count = sel ? parseInt(sel.value, 10) : 8;
        this.startSession(count || 8);
      }
    }

    while (this.accumulator >= ENGINE.FIXED_DT) {
      this.updatePhysics(ENGINE.FIXED_DT);
      this.accumulator -= ENGINE.FIXED_DT;
    }

    this.render();

    if (this.isRunning) {
      requestAnimationFrame(time => this.loop(time));
    }
  }

  private updatePhysics(dt: number): void {
    if (this.state.phase !== 'PLAYING' && this.state.phase !== 'COUNTDOWN') {
      return;
    }

    this.state.frameCounter++;
    this.particles.update();
    this.arena.update();
    this.hud.update();

    const human = this.state.humanPlayer;
    if (human && this.input.consumeSmash()) {
      human.triggerSmash();
    }

    // Update human and bot paddles
    const moveInput = this.input.getMove();
    const tiltInput = this.input.getTilt();
    const numSides = this.state.paddles.length;

    for (const pad of this.state.paddles) {
      pad.updatePhysics(dt, numSides, moveInput, tiltInput);
    }

    const edges = this.arena.getAllEdges(numSides);
    BotController.updateBots(
      this.state.paddles,
      edges,
      this.state.balls,
      this.arena.radius,
      this.state.initialPlayerCount,
      this.state.frameCounter,
      this.state.isCountingDown
    );

    if (!this.state.isCountingDown) {
      // 1. Spawn Powerups every 1500 frames (~25s)
      if (this.state.frameCounter % POWERUP.SPAWN_INTERVAL_TICKS === 0) {
        const inradius = this.arena.radius * Math.cos(Math.PI / Math.max(3, numSides));
        const maxDist = Math.max(30, inradius * 0.7);
        const angle = Math.random() * Math.PI * 2;
        const dist = 15 + Math.random() * (maxDist - 15);
        const spawnPos = this.arena.center.add(Vec2.fromAngle(angle, dist));

        const rand = Math.random();
        let pType = PowerUpType.ONE_UP;
        if (rand > 0.75) pType = PowerUpType.EMP_FREEZE;
        else if (rand > 0.5) pType = PowerUpType.SHIELD;
        else if (rand > 0.25) pType = PowerUpType.MULTIBALL;

        this.state.powerups.push(new PowerUp(spawnPos, pType));
      }

      // 2. Powerups update
      for (let p = this.state.powerups.length - 1; p >= 0; p--) {
        const pu = this.state.powerups[p];
        pu.update();
        if (pu.isExpired) {
          this.state.powerups.splice(p, 1);
        }
      }

      // 3. Black Hole anti-stall
      const mainBall = this.state.balls.find(b => b.isMain);
      if (
        mainBall &&
        mainBall.hitHistory.length === 5 &&
        !this.state.blackHole.active
      ) {
        const uniqueHits = new Set(mainBall.hitHistory);
        if (uniqueHits.size === 2) {
          const inradius = this.arena.radius * Math.cos(Math.PI / Math.max(3, numSides));
          const maxDist = Math.max(25, inradius * 0.65);
          const randAngle = Math.random() * Math.PI * 2;
          const randDist = 15 + Math.random() * (maxDist - 15);
          const holePos = this.arena.center.add(Vec2.fromAngle(randAngle, randDist));
          this.state.blackHole.spawn(holePos);
          mainBall.hitHistory = [];
        }
      }

      this.state.blackHole.update(this.state.balls, this.arena.radius);

      // 4. Balls update
      for (let b = this.state.balls.length - 1; b >= 0; b--) {
        const ball = this.state.balls[b];
        ball.update();
        if (ball.isDead && !ball.isMain) {
          this.particles.spawnParticles(ball.pos, '#ffff00', 15);
          this.state.balls.splice(b, 1);
        }
      }

      // 5. Collisions
      CollisionSystem.resolvePowerUpCollisions(
        this.state.balls,
        this.state.powerups,
        this.state.paddles
      );

      CollisionSystem.resolveBallCollisions(
        this.state.balls,
        this.state.paddles,
        this.arena,
        (loserIndex, ball) => this.handleLoss(loserIndex, ball)
      );

      // Clean up dead non-main balls after collisions
      for (let b = this.state.balls.length - 1; b >= 0; b--) {
        const bObj = this.state.balls[b];
        if (bObj && bObj.isDead && !bObj.isMain) {
          this.state.balls.splice(b, 1);
        }
      }
    }
  }

  private render(): void {
    this.renderer.render(
      this.arena,
      this.state.paddles,
      this.state.balls,
      this.state.powerups,
      this.state.blackHole,
      this.particles,
      this.state.frameCounter,
      this.state.isCountingDown,
      this.state.countdownValue,
      this.state.canvasMessage
    );
  }
}
